import { apiFetch } from './http';

// ─────────────────────────────────────────────────────────────────────
// WHERE THIS PERSON PLAYS (#710).
//
// `GET /me/clubs` — and the endpoint's own comment says why it exists: *«a
// different question from `/auth/scopes`, and the difference is the whole
// point of the player shell.»*
//
//   /auth/scopes   where may I ACT as an administrator — `tenant_memberships`
//   /me/clubs      where am I ON THE ROSTER — `tenant_players`
//
// One person may have both, either or neither. The player home read `scopes`
// before this module existed, which answered the ADMINISTRATOR's question: an
// ordinary club member holds no membership row, so the home told somebody who
// plays at a club twice a week that they belong to none.
//
// The endpoint has existed since #749 and had no client at all — it had never
// been called from the browser.
// ─────────────────────────────────────────────────────────────────────

/** One club this person PLAYS at. Never one they merely administer. */
export interface MyClub {
  /**
   * The organization id — what entering the club addresses (#800).
   *
   * The member surface names an organization by id (`X-Starpoint-Tenant`), so
   * a list without one can be read and not entered. It grants nothing:
   * `memberContextFromAccount` validates the roster row for whichever
   * organization a request names.
   */
  id: string;
  slug: string;
  name: string;
  kind: string;
}

/**
 * The outcome, as one flat shape.
 *
 * ── «NONE» AND «WE COULD NOT ASK» ARE DIFFERENT ANSWERS ───────────
 *
 * `clubs: []` with `failed: false` is the server saying this person is on no
 * roster. `failed: true` is the question not having been answered, and the
 * home must not turn the second into the first — the remedy for one is to
 * find a club, and for the other it is to try again. `ScopeStatus` draws the
 * same line for scopes and says so in its own comments.
 */
export interface MyClubsResult {
  clubs: MyClub[];
  failed: boolean;
}

export async function fetchMyClubs(): Promise<MyClubsResult> {
  let res: Awaited<ReturnType<typeof apiFetch>>;
  try {
    res = await apiFetch('/me/clubs');
  } catch {
    return { clubs: [], failed: true };
  }
  if (!res.ok) return { clubs: [], failed: true };
  const body = (await res.json().catch(() => null)) as { clubs?: unknown } | null;
  if (!Array.isArray(body?.clubs)) return { clubs: [], failed: true };

  // SHAPED, NOT TRUSTED. A row without a name would render as an empty line in
  // a list of clubs, which reads as a club whose name nobody knows.
  //
  // AND A ROW WITH NO ID IS DROPPED TOO (#800), for the same kind of reason
  // one step on: it could be listed and not entered, so tapping it would do
  // nothing. A club that cannot be entered is not a club this list should
  // offer — and a server that stopped sending the id would show an empty list
  // rather than a dead one, which is a visible failure instead of a silent.
  const clubs: MyClub[] = [];
  for (const row of body.clubs) {
    if (!row || typeof row !== 'object') continue;
    const r = row as Record<string, unknown>;
    if (typeof r.id !== 'string' || !r.id) continue;
    if (typeof r.slug !== 'string' || typeof r.name !== 'string') continue;
    clubs.push({
      id: r.id,
      slug: r.slug,
      name: r.name,
      kind: typeof r.kind === 'string' ? r.kind : '',
    });
  }
  return { clubs, failed: false };
}
